import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { checkAuthStatus, logout, type AuthCheckResult } from '@/utils/auth'
import { getTestUsersApi } from '@/api/authApi'

// 쿼리 키 상수
export const AUTH_QUERY_KEYS = {
  auth: ['auth'] as const,
  status: () => [...AUTH_QUERY_KEYS.auth, 'status'] as const
} as const

/**
 * 인증 상태 확인 쿼리
 * - 5분마다 자동 재검증
 * - 백그라운드에서 자동 새로고침
 * - 에러 발생 시 3회까지 재시도
 */
export const useAuthStatus = () => {
  return useQuery({
    queryKey: AUTH_QUERY_KEYS.status(),
    queryFn: checkAuthStatus,
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: (failureCount, error) => {
      // 특정 에러의 경우 재시도하지 않음
      if (
        error &&
        typeof error === 'object' &&
        'step' in error &&
        'authenticated' in error &&
        'needsRedirect' in error
      ) {
        const authError = error as AuthCheckResult
        if (authError.step === 'no_access_token' || authError.step === 'access_token_invalid') {
          return false
        }
      }
      return failureCount < 3
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000) // 지수 백오프
  })
}

/**
 * 로그아웃 뮤테이션
 * - 성공 시 모든 쿼리 캐시 무효화
 */
export const useLogout = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await logout()
    },
    onSuccess: () => {
      // 모든 캐시 무효화
      queryClient.clear()
    },
    onError: async error => {
      console.error('로그아웃 중 오류:', error)
      // 에러가 발생해도 강제 로그아웃
      await logout()
      queryClient.clear()
    }
  })
}

/**
 * 인증 상태 수동 새로고침
 */
export const useRefreshAuth = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: checkAuthStatus,
    onSuccess: data => {
      // 캐시 업데이트
      queryClient.setQueryData(AUTH_QUERY_KEYS.status(), data)
    },
    onError: error => {
      console.error('인증 새로고침 실패:', error)
    }
  })
}

/**
 * 인증 상태 캐시 무효화
 */
export const useInvalidateAuth = () => {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({
      queryKey: AUTH_QUERY_KEYS.auth
    })
  }
}

export const useTestUsers = () => {
  return useQuery({
    queryKey: ['test-users'],
    queryFn: getTestUsersApi
  })
}
